import React, { useCallback, useEffect, useRef, useState } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { useLocation } from 'react-router-dom';
import { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';

import MotorNameEditor from './MotorNameEditor';
import MotorInformation from './MotorInformation';
import EmailFrequencyComponent from './EmailFrequencyComponent';
import DisplayUnitSettings from './DisplayUnitSettings';
import CameraSnapshot from './CameraSnapshot';
import GraphSection from './GraphSection';
import AlertSettings from './AlertSettings';
import {
  toConsumptionSeries,
  aggregateByDay,
  aggregateByMonth,
  aggregateByYear,
  getDailyTotals,
} from './consumptionUtils';
import { exportWaterReportPDF, exportWaterReportExcel } from './waterExport';
import { fetchCameraById, fetchTramesCamera } from './cameraApi';

registerLocale('fr', fr);

export default function WaterMeterDashboard(props) {
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : props.user;
  const location = useLocation();

  const [motor, setMotor] = useState(location.state?.boitier || {});
  const [loadingMotor, setLoadingMotor] = useState(true);

  const [unit, setUnit] = useState('m3');
  const unitLabel = unit === 'litre' ? 'L' : 'm³';

  const today = new Date();
  const defaultStart = moment(today).subtract(30, 'days').toDate();

  const [dayRange, setDayRange] = useState({ start: defaultStart, end: today });
  const [monthRange, setMonthRange] = useState({ start: defaultStart, end: today });
  const [yearRange, setYearRange] = useState({ start: defaultStart, end: today });

  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [dailyTotals, setDailyTotals] = useState([]);

  const [loadingDay, setLoadingDay] = useState(false);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loadingYear, setLoadingYear] = useState(false);

  const dayChartRef = useRef();
  const monthChartRef = useRef();
  const yearChartRef = useRef();

  useEffect(() => {
    const loadCamera = async () => {
      setLoadingMotor(true);
      try {
        const camera = await fetchCameraById(motor.id, user);
        if (camera) {
          setMotor((prev) => ({
            ...prev,
            id: camera._id,
            name: camera.nom_camera,
            clientLoc: camera.clientLoc,
            depassement: camera.depassement,
            frequenceRelance: camera.frequenceRelance,
            dateDepassement: camera.dateDepassement,
          }));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de la camera :', error);
      } finally {
        setLoadingMotor(false);
      }
    };
    if (motor.id) loadCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConsumptionSeries = useCallback(
    async (start, end) => {
      const d1 = moment(start).subtract(1, 'day').format('YYYY-MM-DD');
      const d2 = moment(end).add(1, 'day').format('YYYY-MM-DD');
      const trames = await fetchTramesCamera(motor.id, d1, d2, user.token);
      return toConsumptionSeries(trames);
    },
    [motor.id, user.token]
  );

  const handleSearchDay = useCallback(
    async (start, end) => {
      setDayRange({ start, end });
      setLoadingDay(true);
      try {
        const series = await fetchConsumptionSeries(start, end);
        setDailyData(aggregateByDay(series, start, end, unit));
      } catch (error) {
        console.error('Erreur lors de la récupération des trames (jour) :', error);
      } finally {
        setLoadingDay(false);
      }
    },
    [fetchConsumptionSeries, unit]
  );

  const handleSearchMonth = useCallback(
    async (start, end) => {
      setMonthRange({ start, end });
      setLoadingMonth(true);
      try {
        const series = await fetchConsumptionSeries(start, end);
        setMonthlyData(aggregateByMonth(series, start, end, unit));
      } catch (error) {
        console.error('Erreur lors de la récupération des trames (mois) :', error);
      } finally {
        setLoadingMonth(false);
      }
    },
    [fetchConsumptionSeries, unit]
  );

  const handleSearchYear = useCallback(
    async (start, end) => {
      setYearRange({ start, end });
      setLoadingYear(true);
      try {
        const series = await fetchConsumptionSeries(start, end);
        setYearlyData(aggregateByYear(series, start, end, unit));
      } catch (error) {
        console.error('Erreur lors de la récupération des trames (année) :', error);
      } finally {
        setLoadingYear(false);
      }
    },
    [fetchConsumptionSeries, unit]
  );

  useEffect(() => {
    if (!motor.id) return;
    handleSearchDay(dayRange.start, dayRange.end);
    handleSearchMonth(monthRange.start, monthRange.end);
    handleSearchYear(yearRange.start, yearRange.end);

    const loadDailyTotals = async () => {
      try {
        const start = moment(today).subtract(1, 'year').toDate();
        const series = await fetchConsumptionSeries(start, today);
        setDailyTotals(getDailyTotals(series));
      } catch (error) {
        console.error("Erreur lors du chargement de l'historique des dépassements :", error);
      }
    };
    loadDailyTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor.id]);

  useEffect(() => {
    if (!motor.id) return;
    handleSearchDay(dayRange.start, dayRange.end);
    handleSearchMonth(monthRange.start, monthRange.end);
    handleSearchYear(yearRange.start, yearRange.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const handleExportPDF = async () => {
    await exportWaterReportPDF({ motor, refs: [dayChartRef, monthChartRef, yearChartRef] });
  };

  const handleExportExcel = () => {
    exportWaterReportExcel({
      motor,
      unitLabel,
      sheets: [
        { name: 'Journalier', data: dailyData },
        { name: 'Mensuel', data: monthlyData },
        { name: 'Annuel', data: yearlyData },
      ],
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 text-gray-900">
      <Header user={user} />
      <div className="flex-grow flex items-center justify-center py-12">
        <div className="max-w-4xl w-full px-4 sm:px-0">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg mb-8">
            <MotorNameEditor motor={motor} setMotor={setMotor} user={user} />
          </div>

          <MotorInformation motor={motor} user={user} setMotor={setMotor} />
          <CameraSnapshot motor={motor} user={user} />
          <EmailFrequencyComponent user={user} motor={motor} />
          <DisplayUnitSettings unit={unit} setUnit={setUnit} />

          <AlertSettings
            motor={motor}
            user={user}
            setMotor={setMotor}
            loadingMotor={loadingMotor}
            dailyTotals={dailyTotals}
          />

          <GraphSection
            title="Volume journalier"
            colorKey="journalier"
            data={dailyData}
            unitLabel={unitLabel}
            startDate={dayRange.start}
            endDate={dayRange.end}
            onSearch={handleSearchDay}
            chartRef={dayChartRef}
            loading={loadingDay}
          />

          <GraphSection
            title="Volume mensuel"
            colorKey="mensuel"
            data={monthlyData}
            unitLabel={unitLabel}
            startDate={monthRange.start}
            endDate={monthRange.end}
            onSearch={handleSearchMonth}
            chartRef={monthChartRef}
            loading={loadingMonth}
          />

          <GraphSection
            title="Volume annuel"
            colorKey="annuel"
            data={yearlyData}
            unitLabel={unitLabel}
            startDate={yearRange.start}
            endDate={yearRange.end}
            onSearch={handleSearchYear}
            chartRef={yearChartRef}
            loading={loadingYear}
          />

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleExportPDF}
              className="bg-green-500 text-white py-3 px-6 rounded-lg shadow-lg hover:bg-green-600 transition duration-200"
            >
              Exporter en PDF
            </button>
            <button
              onClick={handleExportExcel}
              className="bg-blue-500 text-white py-3 px-6 rounded-lg shadow-lg hover:bg-blue-600 transition duration-200"
            >
              Exporter en Excel
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}